import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { persistLead, captureBlockedLead } from "@/lib/lead-fallback.server";
import { confirmationEmail, internalLeadEmail, LEAD_INBOX, LEAD_NOTIFY, sendEmail } from "@/lib/email/resend.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";
import { getLeadMagnet, LEAD_MAGNETS } from "@/content/lead-magnets";

const VALID_SLUGS = Object.keys(LEAD_MAGNETS) as [string, ...string[]];

const Schema = z.object({
  magnet_slug: z.enum(VALID_SLUGS).default("formatguide-2026"),
  email: z.string().email().max(255),
  company: z.string().max(120).optional().nullable(),
  source_path: z.string().max(255).optional().nullable(),
  utm_source: z.string().max(80).optional().nullable(),
  utm_medium: z.string().max(80).optional().nullable(),
  utm_campaign: z.string().max(120).optional().nullable(),
  funnel_id: z.string().max(64).optional().nullable(),
  user_agent: z.string().max(500).optional().nullable(),
  session_id: z.string().uuid().nullable().optional(),
  visitor_id: z.string().uuid().nullable().optional(),
});

export type RequestGuideResult = {
  status: "released" | "notifyOnly";
  url: string | null;
  filename: string | null;
  title: string;
};

export const requestGuide = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => Schema.parse(input))
  .handler(async ({ data }): Promise<RequestGuideResult> => {
    const magnet = getLeadMagnet(data.magnet_slug)!;
    const ip = getClientIp(getRequest());
    const rateOk = checkRateLimit({
      scope: `lead-magnet:${magnet.slug}`,
      keys: [ip, data.email],
      limit: 5,
      windowMs: 60 * 60 * 1000,
    });

    const baseResult: RequestGuideResult = {
      status: magnet.status,
      url: magnet.status === "released" ? magnet.pdfHref ?? null : null,
      filename: magnet.downloadFilename ?? null,
      title: magnet.title,
    };

    if (!rateOk) {
      // Returnera ändå download-URL för released så användaren inte blockas helt,
      // men spara förfrågan så den syns i adminpanelen.
      await captureBlockedLead({
        flow: `lead-magnet:${magnet.slug}`,
        reason: "rate-limit",
        email: data.email,
        company: data.company ?? null,
        source_path: data.source_path ?? null,
        user_agent: data.user_agent ?? null,
        session_id: data.session_id ?? null,
        visitor_id: data.visitor_id ?? null,
        extra: { magnet_slug: magnet.slug },
      });
      return baseResult;
    }

    try {
      const { magnet_slug, ...rest } = data;
      await persistLead(
        "lead_magnet_requests",
        { magnet_slug, ...rest },
        {
          source: `lead-magnet:${magnet_slug}`,
          email: data.email,
          company: data.company ?? null,
          source_path: data.source_path ?? null,
          session_id: data.session_id ?? null,
          visitor_id: data.visitor_id ?? null,
        },
      );
    } catch (e) {
      console.error("[lead-magnet] unexpected:", e);
    }

    const isNotify = magnet.status === "notifyOnly";
    const internal = internalLeadEmail({
      subject: `${isNotify ? "[NOTIFY] " : ""}${magnet.title} — ${data.email}`,
      heading: isNotify
        ? `Notifiera-mig: ${magnet.title}`
        : `Ny nedladdning: ${magnet.title}`,
      fields: {
        Guide: magnet.title,
        Status: isNotify ? "Vill bli notifierad" : "Laddade ner",
        "E-post": data.email,
        Företag: data.company,
        Källa: data.source_path,
        Kampanj: data.utm_campaign,
      },
    });
    const confirm = confirmationEmail({
      subject: isNotify
        ? `Vi hör av oss när ${magnet.title} släpps`
        : `Din guide: ${magnet.title}`,
      heading: isNotify
        ? `Vi hör av oss när ${magnet.title} släpps`
        : `Din guide: ${magnet.title}`,
      intro: isNotify ? magnet.notifyIntro ?? magnet.confirmIntro : magnet.confirmIntro,
    });

    await Promise.allSettled([
      sendEmail({
        to: LEAD_NOTIFY,
        subject: internal.subject,
        html: internal.html,
        replyTo: data.email,
        logContext: { kind: "internal_lead", leadSource: "lead_magnet", leadEmail: data.email },
      }),
      sendEmail({
        to: data.email,
        subject: confirm.subject,
        html: confirm.html,
        replyTo: LEAD_INBOX,
        logContext: { kind: "lead_confirmation", leadSource: "lead_magnet", leadEmail: data.email },
      }),
    ]);

    return baseResult;
  });

// Bakåtkompatibel wrapper för befintliga anropare (t.ex. /kunskap).
export const requestFormatguide = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    Schema.parse({ ...(input as object), magnet_slug: "formatguide-2026" }),
  )
  .handler(async ({ data }) => {
    const res = await requestGuide({ data });
    return { url: (res.url ?? "/downloads/fenny-formatguide-2026.pdf") as string };
  });
